import {AssertUnreachable, dayInMS, hourInMS} from "web-vcore";
import {Range} from "js-vextensions";
import {RangeType, StatView} from "../../Server/DB/statViews/@StatView.js";
import {StatsXType, StatsXType_tickStepInfos} from "../../Server/DB/statViews/@StatTypes.js";

export function JStatsUI_GetInitialTicks(view: StatView) {
	const xTicksInfo = StatsXType_tickStepInfos.get(view.xType as any);
	let result = xTicksInfo?.xTicks_override?.slice() ?? xTicksInfo?.xTicks_initial?.(view) ?? [];

	const xAxis_stepOverride_fromUserView = view.xAxis_stepOverride_enabled ? view.xAxis_stepOverride : null;
	if (xAxis_stepOverride_fromUserView) {
		result = result.map(a=>a.RoundTo(xAxis_stepOverride_fromUserView)).Distinct();
	}

	return result;
}

export function ExtendedXTicksForSmoothing(xTicks: number[], view: StatView, includeSmoothing: boolean) {
	const {xType} = view;
	if (view.smoothing == null) return xTicks;
	if (xTicks.length == 0) return xTicks; // if x-ticks is empty, that means no data, so no need to extend it

	const xTicksInfo = StatsXType_tickStepInfos.get(view.xType as any);
	const extraLeftRangeForTimeBased = includeSmoothing
		? (view.smoothingType == "previous" ? view.smoothing : Math.ceil(view.smoothing / 2)) // todo: make sure Math.ceil is correct
		: 0;

	const oldMin = xTicks.First();
	const oldMax = xTicks.Last();
	if (xTicksInfo?.xTicks_prependToInitialForSmoothing) {
		const stepOverride = (view.xAxis_stepOverride_enabled ? view.xAxis_stepOverride : null) ?? xTicksInfo?.xTicks_stepOverride;
		return Range(oldMin - extraLeftRangeForTimeBased, oldMax, stepOverride ?? 1);
	} else {
		return xTicks;
	}
}

export function GetLeftEdgeDayOffsetForDayRange(rangeType: RangeType, start: number, count: number) {
	if (rangeType == "start") {
		//return moment(start).startOf("day").diff(moment().startOf("day"), "days");
		return GetAnchorStatDayOffset(start, "day");
	}
	if (rangeType == "count") return -count;
	AssertUnreachable(rangeType);
}

// stat-days
// ==========

/** Consider the "start of the current stat-day" to be: the most recent 3am that's <= the current time. (the raw-date of that 3am is also the "date"/anchor-stat-day assigned to the stat-day)
 * Why choose 3am? Because the user is very unlikely to start a day-session just prior to this time, so it's a good "reset" point.
 * Note: Each session-period of launch-type "day" are bucketed into these stat-days based on which period contains the start-time. (see GetStartOfCurrentStatNight for bucketing of "night" session-periods) */
export function GetStatDayContainingX(targetTime: number) {
	let startOfCurrentStatDay = new Date(targetTime).setHours(3, 0, 0, 0);
	if (startOfCurrentStatDay > targetTime) startOfCurrentStatDay -= dayInMS;
	return startOfCurrentStatDay;
}
/** Consider the "start of the current stat-night" to be: the most recent 3pm that's <= the current time. (the raw-date of that 3pm is also the "date"/anchor-stat-day assigned to the stat-night)
 * Why choose 3pm? Because the user is very unlikely to start a night-session just prior to this time, so it's a good "reset" point.
 * Note: Each session-period of launch-type "night" are bucketed into these stat-nights based on which period contains the start-time. (see GetStartOfCurrentStatDay for bucketing of "day" session-periods) */
export function GetStatNightContainingX(targetTime: number) {
	let startOfCurrentStatNight = new Date(targetTime).setHours(15, 0, 0, 0);
	if (startOfCurrentStatNight > targetTime) startOfCurrentStatNight -= dayInMS;
	return startOfCurrentStatNight;
}

/** Note: If the target-time is "associated with" a larger period, which already has its "start-time anchor" established (eg. if this is an event within a session-period), calling this may give inconsistent results!
 * For cases like that, consider using GetAnchorStatDayForTargetTime. */
export function GetStatDayOrNightContainingTimeX(targetTime: number, sessionType: "day" | "night") {
	return sessionType == "day" ? GetStatDayContainingX(targetTime) : GetStatNightContainingX(targetTime);
}
export function GetAnchorStatDayForTargetTime(targetTime: number, sessionType: "day" | "night") {
	// the "day" case is simple, since stat-days are the date-anchors
	if (sessionType == "day") {
		return GetStatDayContainingX(targetTime);
	}
	// the "night" case "backtracks" to the most recent stat-day prior to itself
	const startOfStatNight = GetStatNightContainingX(targetTime);
	const startOfStatDay = GetStatDayAnchorForStatNight(startOfStatNight);
	return startOfStatDay;
}

/*export function GetStatDayOffset(targetTime: number, sessionType: "day" | "night", now = Date.now(), floorResult = true) {
	const latestStatDay_start = GetStartOfStatDayContainingX(now); // get current stat-day (stat-day anchors stats-graphs rather that stat-nights, regardless of current session-type)
	//const latestStatDay_night_start = latestStatDay_start + (12 * hourInMS); // stat-day start is 3am, so go forward 12 hours to find its stat-night start-time (3pm)

	let startOfStatDayOrNightWithOffset0 = sessionType == "day" ? latestStatDay_start : latestStatDay_night_start;
	let dayOffset = (targetTime - startOfStatDayOrNightWithOffset0) / dayInMS;
	if (floorResult) dayOffset = Math.floor(dayOffset);
	return dayOffset;
}*/

/** The `sessionType` argument determines whether bucketing occurs based on the stat-day separator (3am) or the stat-night separator (3pm). [see GetAnchorStatDayForTargetTime for what "bucketing" implies]
 * Note that due to session-type-specific separators, it is possible (albeit rare) for a "night session in day 1" to be started later than a "day session in day 2". (eg. night-session started Mon 9am, day-session started Mon 8am) */
export function GetAnchorStatDayOffset(targetTime: number, sessionType: "day" | "night", now = Date.now()) {
	const latestStatDay_start = GetStatDayContainingX(now); // get current stat-day (stat-day anchors stats-graphs rather that stat-nights, regardless of current session-type)
	const targetTime_statDay_start = GetAnchorStatDayForTargetTime(targetTime, sessionType);

	// I think Math.floor would also work fine, but use Math.round (just in case of possible interaction with DST, leap-seconds, etc.)
	const dayOffset = Math.round((targetTime_statDay_start - latestStatDay_start) / dayInMS);
	return dayOffset;
}

// these functions are not really needed, but improve clarity of intent slightly
export function GetStatNightAnchoredToStatDay(statDayStartTime: number) {
	return statDayStartTime + (dayInMS / 2); // 3am -> 3pm
}
export function GetStatDayAnchorForStatNight(statNightStartTime: number) {
	return statNightStartTime - (dayInMS / 2); // 3pm -> 3am
}
export function GetStatNightBeforeStatDay(statDayStartTime: number) {
	return statDayStartTime - (dayInMS / 2); // 3am -> 3pm (previous day)
}
export function GetStatDayAfterStatNight(statNightStartTime: number) {
	return statNightStartTime + (dayInMS / 2); // 3pm -> 3am (next day)
}